import { ChevronRight } from 'lucide-react';
import { useRef, useEffect } from 'react';
import timiPicture from '../assets/timi.jpeg';
import { useTranslations } from '../context/TranslationContext';
import useAnalytics from '../hooks/useAnalytics';

export const Hero: React.FC = () => {
    const { t } = useTranslations();
    const { trackEvent } = useAnalytics();
    const sectionRef = useRef<HTMLElement>(null);
    const viewStartTime = useRef<number | null>(null);

    const scrollToContact = () => {
        trackEvent('hero_scroll_to_contact', {
            current_scroll_position: window.scrollY,
            timestamp: new Date().toISOString()
        });

        const element = document.getElementById('contact');
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
        }
    };

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting && !viewStartTime.current) {
                        viewStartTime.current = Date.now();
                        trackEvent('hero_view_start', {
                            timestamp: new Date().toISOString()
                        });
                    } else if (!entry.isIntersecting && viewStartTime.current) {
                        const viewDuration = Date.now() - viewStartTime.current;
                        trackEvent('hero_view_complete', {
                            view_duration_ms: viewDuration,
                            view_duration_readable: `${(viewDuration / 1000).toFixed(1)}s`,
                            timestamp: new Date().toISOString()
                        });
                        viewStartTime.current = null;
                    }
                });
            },
            { threshold: 0.5 }
        );

        if (sectionRef.current) {
            observer.observe(sectionRef.current);
        }

        return () => observer.disconnect();
    }, [trackEvent]);

    return (
        <section id="home" className="pt-24 md:pt-32 bg-slate-50 text-gray-800" ref={sectionRef}>
            <div className="container mx-auto px-6">
                <div className="flex flex-col md:flex-row items-center justify-between pb-12 md:pb-24">
                    <div className="md:w-1/2 text-left md:pr-12">
                        <h1 className="text-4xl md:text-5xl font-bold mb-6">
                            {t.hero.title}
                        </h1>
                        <p className="text-lg mb-8 text-gray-600">
                            {t.hero.description}
                            <br /> <br />
                            <span className="text-[#597F70] font-bold">
                                {t.hero.highlighted_description}
                            </span>
                        </p>
                        <button
                            onClick={scrollToContact}
                            className="bg-[#597F70] text-white px-10 py-4 rounded-lg text-lg font-semibold hover:bg-[#597F70] transition-colors flex items-center"
                            aria-label={t.hero.cta}
                        >
                            {t.hero.cta}
                            <ChevronRight className="ml-2 h-6 w-6" />
                        </button>
                    </div>
                    <div className="md:w-1/2 mt-8 md:mt-0 flex justify-center">
                        <div className="relative w-72 h-72 md:w-[500px] md:h-[500px] bg-white p-4 rounded-lg shadow-lg">
                            <img
                                src={timiPicture}
                                alt={t.hero.imageAlt}
                                className="w-full h-full object-cover rounded-lg"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};